/**
 * Pre-Consultation tab – form layout per design: two-column Pre-Consultation Form,
 * Current Medications, Lab Results; light grey inputs, blue Clear/Save buttons, required asterisks.
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  InputLabel,
  TextField,
  Select,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/FileUpload';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import FormField from '../Common/FormField';
import RightPanelTabs from '../RightPanel/RightPanelTabs';
import { RIGHT_PANEL_WIDTH, RIGHT_PANEL_WIDTH_XS } from '../RightPanel/constants';
import AmbientMicButton from '../AmbientMicButton/AmbientMicButton';
import { useFormData } from '../../contexts/FormDataContext';
import { useConfidenceToggle } from '../../contexts/ConfidenceToggleContext';
import { useSession } from '../../contexts/SessionContext';
import { useUser } from '../../contexts/UserContext';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import PersonIcon from '@mui/icons-material/Person';
import WorkflowSessionBar from '../Workflow/WorkflowSessionBar';
import LabReportUpload from '../LabReportUpload/LabReportUpload';

import GlucoseLogUpload from '../GlucoseLogUpload/GlucoseLogUpload';

const TAB_NAME = 'PreConsultation';

const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': { borderColor: '#8DBAEA', borderWidth: 1 },
    '&:hover fieldset': { borderColor: '#7aa8e0' },
    '&.Mui-focused fieldset': { borderColor: '#012676', borderWidth: 1 },
  },
  '& .MuiFormLabel-asterisk': { color: '#d32f2f' },
};

const selectFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': { borderColor: '#8DBAEA', borderWidth: 1 },
    '&:hover fieldset': { borderColor: '#7aa8e0' },
  },
  '& .MuiFormLabel-asterisk': { color: '#d32f2f' },
};

const formContainerSx = {
  border: '1px solid #8DBAEA',
  borderRadius: 1.5,
  ...formFieldSx,
};

export default function PreConsultationPage() {
  const { saveFormData, clearFormForTab, updateField, getFieldValue } = useFormData();
  const { showConfidence, setShowConfidence } = useConfidenceToggle();
  const { session, isEditingHistoricalForms, effectiveFormSessionId } = useSession();
  const { patient } = useUser();
  const { canAccess } = useFeatureAccess();
  const canViewGlucoseLog = canAccess('patient_visit.glucose_log');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState('');

  const [labUploadOpen, setLabUploadOpen] = useState(false);
  const [patientReportedSummary, setPatientReportedSummary] = useState('');
  const [uploadedGlucoseRows, setUploadedGlucoseRows] = useState<any[]>([]);

  useEffect(() => {
    setUploadedGlucoseRows([]);
    setPatientReportedSummary('');
  }, [patient?.patient_id]);
  const formatLabSummary = (summary: string) => {
  if (!summary) return '';

  return summary
    .split(',')
    .map((item) => {
      const trimmed = item.trim();

      let status = 'Within Range';

      const lower = trimmed.toLowerCase();

      // HIGH CONDITIONS
      if (
        lower.includes('high') ||
        lower.includes('elevated') ||
        lower.includes('above')
      ) {
        status = 'High';
      }

      // LOW CONDITIONS
      else if (
        lower.includes('low') ||
        lower.includes('below')
      ) {
        status = 'Low';
      }

      return `${trimmed} — ${status}`;
    })
    .join('\n\n');
};

  useEffect(() => {
  const savedSummary = getFieldValue(
    TAB_NAME,
    'patient_reported_lab_results'
  );

  console.log(
    '[PRECONSULTATION] Loaded saved lab summary:',
    savedSummary
  );

  if (savedSummary) {
    setPatientReportedSummary(savedSummary);
  }
}, [getFieldValue]);

  const visitDate = getFieldValue(TAB_NAME, 'visit_date');
  useEffect(() => {
    if (isEditingHistoricalForms) return;
    if (visitDate == null || String(visitDate).trim() === '') {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      updateField(TAB_NAME, 'visit_date', `${yyyy}-${mm}-${dd}`);
    }
  }, [isEditingHistoricalForms, visitDate, updateField]);

  const arrivalTime = getFieldValue(TAB_NAME, 'arrival_time');
  useEffect(() => {
    if (isEditingHistoricalForms) return;
    if (arrivalTime == null || String(arrivalTime).trim() === '') {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      updateField(TAB_NAME, 'arrival_time', `${hh}:${mm}`);
    }
  }, [isEditingHistoricalForms, arrivalTime, updateField]);

  const handleClear = () => {
    clearFormForTab(TAB_NAME);
    setSaveStatus('idle');
    setSaveMessage('');
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveMessage('');
    const result = await saveFormData();
    if (result.success) {
      setSaveStatus('success');
      setSaveMessage('Pre-consultation saved.');
    } else {
      setSaveStatus('error');
      setSaveMessage(result.error || 'Save failed');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* ── LEFT: Pre-Consultation form ── */}
      <Box
        sx={{
          flex: { xs: '1 1 0%', md: '1 1 65%' },
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <WorkflowSessionBar />
        </Box>

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A4D9A', fontFamily: 'inherit' }}>
              Pre-Consultation
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575', mt: 0.5, fontFamily: 'inherit' }}>
              Input patient information for pre-consultation.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ bgcolor: '#eef3fb', borderRadius: 20, px: 1.5, py: 0.2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showConfidence}
                    onChange={(_, v) => setShowConfidence(v)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#008AD2' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#008AD2' },
                    }}
                  />
                }
                label="AI Confidence"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: '#4a4a4a', fontFamily: 'inherit' },
                }}
              />
            </Box>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: '6px',
                border: '1px solid #8DBAEA',
                px: 2,
                py: 1.25,
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <PersonIcon sx={{ color: '#1A4D9A', fontSize: 22 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#666666', display: 'block', fontFamily: 'inherit' }}>
                  Patient
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333333', fontFamily: 'inherit' }}>
                  {patient?.name || 'No patient selected'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#757575', fontFamily: 'inherit' }}>
                  MRN: {patient?.mrn || '–'}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* ── Main Form Paper ── */}
        <Paper sx={{ p: 2.5, flex: '0 0 auto', alignSelf: 'stretch', ...formContainerSx }}>		
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#424242', mb: 2 }}>
            Pre-Consultation Form
          </Typography>

          {/* ── Two-column vitals section ──
              CRITICAL: parent Box must be display:flex + alignItems:stretch
              so the border of Paper grows to fit the TALLER (right) column.
              Both child boxes must have height:'100%' so they fill equally.   */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'stretch',   // ← key: stretch both cols to same height
              mb: 2,
            }}
          >
            {/* ── LEFT COLUMN ── */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                // No border, no overflow — pure flex column
              }}
            >
              <FormField
                tabName={TAB_NAME}
                fieldName="visit_date"
                label="Visit Date"
                required
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                sx={formFieldSx}
              />
              <FormField
                tabName={TAB_NAME}
                fieldName="arrival_time"
                label="Arrival Time"
                required
                type="time"
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                sx={formFieldSx}
              />
              <FormField
                tabName={TAB_NAME}
                fieldName="reason_for_visit"
                label="Reason for Visit"
                required
                placeholder="Enter reason for visit"
                size="small"
                fullWidth
                sx={formFieldSx}
              />
              <FormControl fullWidth size="small" required sx={selectFieldSx}>
                <InputLabel>Triage Category</InputLabel>
                <Select
                  label="Triage Category"
                  value={getFieldValue(TAB_NAME, 'triage_category') || ''}
                  onChange={(e) => updateField(TAB_NAME, 'triage_category', e.target.value)}
                  sx={{ textAlign: 'left', bgcolor: '#ffffff' }}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Routine">Routine</MenuItem>
                  <MenuItem value="Urgent">Urgent</MenuItem>
                  <MenuItem value="Emergency">Emergency</MenuItem>
                  <MenuItem value="Follow-up">Follow-up</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* ── RIGHT COLUMN ── */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {/* Height | Weight */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="height_cm"
                  label="Height (cm)"
                  required
                  type="number"
                  size="small"
                  fullWidth
                  sx={formFieldSx}
                />
                <FormField
                  tabName={TAB_NAME}
                  fieldName="weight_kg"
                  label="Weight (kg)"
                  required
                  type="number"
                  size="small"
                  fullWidth
                  sx={formFieldSx}
                />
              </Box>

              {/* Pulse | Temperature */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="pulse_bpm"
                  label="Pulse (bpm)"
                  required
                  type="number"
                  size="small"
                  fullWidth
                  sx={formFieldSx}
                />
                <FormField
                  tabName={TAB_NAME}
                  fieldName="temperature_c"
                  label="Temperature (°C)"
                  required
                  type="number"
                  size="small"
                  fullWidth
                  sx={formFieldSx}
                />
              </Box>

              {/* BP Diastolic | BP Systolic */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <FormField
                  tabName={TAB_NAME}
                  fieldName="bp_diastolic"
                  label="BP Diastolic"
                  required
                  type="number"
                  size="small"
                  fullWidth
                  sx={formFieldSx}
                />
                <FormField
                  tabName={TAB_NAME}
                  fieldName="bp_systolic"
                  label="BP Systolic"
                  required
                  type="number"
                  size="small"
                  fullWidth
                  sx={formFieldSx}
                />
              </Box>

              {/* Allergy Present */}
              <FormField
                tabName={TAB_NAME}
                fieldName="allergy_present"
                label="Allergy Present"
                placeholder="Enter allergies (e.g., Penicillin, Peanuts) or 'None'"
                multiline
                rows={2}
                size="small"
                fullWidth
                sx={formFieldSx}
              />

              {/* Random Blood Glucose */}
              <FormField
                tabName={TAB_NAME}
                fieldName="random_blood_glucose"
                label="Random Blood Glucose"
                required
                type="number"
                size="small"
                fullWidth
                sx={formFieldSx}
              />
            </Box>
          </Box>
          {/* ── End two-column section ── */}

          {/* ── Full-width fields below ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Current Medications */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#424242', mb: 1 }}>
                Current Medications (Patient Reported)
              </Typography>
              <FormField
                tabName={TAB_NAME}
                fieldName="current_medications"
                placeholder="List current medications"
                required
                multiline
                rows={2}
                size="small"
                fullWidth
                sx={formFieldSx}
              />
            </Box>

            {canViewGlucoseLog && (
              <GlucoseLogUpload
                onUploadComplete={(rows) => {
                  setUploadedGlucoseRows((prev) => [...prev, ...rows]);
                }}
              />
            )}
            {canViewGlucoseLog && uploadedGlucoseRows.length > 0 && (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#424242', mb: 1 }}>
      Uploaded Glucose Logs
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Fasting</TableCell>
            <TableCell>After Breakfast</TableCell>
            <TableCell>Lunch Pre</TableCell>
            <TableCell>Lunch Post</TableCell>
            <TableCell>Dinner Pre</TableCell>
            <TableCell>Dinner Post</TableCell>
            <TableCell>3 AM</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {uploadedGlucoseRows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.date}</TableCell>
              <TableCell>{row.fasting}</TableCell>
              <TableCell>{row.afterBreakfast}</TableCell>
              <TableCell>{row.lunchPre}</TableCell>
              <TableCell>{row.lunchPost}</TableCell>
              <TableCell>{row.dinnerPre}</TableCell>
              <TableCell>{row.dinnerPost}</TableCell>
              <TableCell>{row.threeAM}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)}

            {/* ── Lab Report Upload Dialog ── */}
            <Dialog
              open={labUploadOpen}
              onClose={() => {
                setLabUploadOpen(false);
                document.body.style.pointerEvents = 'auto';
              }}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>Upload Lab Report</DialogTitle>
              <DialogContent>
                <LabReportUpload
                  sessionId={effectiveFormSessionId ?? undefined}
                  patientId={patient?.patient_id}
                  mode="patient_reported"
                  onUploadComplete={(file) => {
                    if ((file as any).lab_summary) {
                      const formattedSummary = formatLabSummary((file as any).lab_summary);
                      setPatientReportedSummary(formattedSummary);
                      updateField(TAB_NAME, 'patient_reported_lab_results', formattedSummary);
                    }
                  }}
                />
              </DialogContent>
            </Dialog>

            {/* ── Lab Result (Patient Reported) ── */}
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#424242' }}>
                  Lab Result (Patient Reported)
                </Typography>
                <Tooltip title="Upload Lab Report">
                  <IconButton
                    size="small"
                    onClick={() => setLabUploadOpen(true)}
                    sx={{ ml: 1, color: '#FFFFFF', bgcolor: '#0D47A1', width: 28, height: 28, '&:hover': { bgcolor: '#1565C0', color: 'white' } }}
                  >
                    <UploadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={patientReportedSummary}
                onChange={(e) => {
                  const value = e.target.value;
                  setPatientReportedSummary(value);
                  updateField(TAB_NAME, 'patient_reported_lab_results', value);
                }}
                placeholder="Uploaded lab insights will appear here. You can also manually edit/add notes."
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFAFA' } }}
              />
            </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{
                borderColor: '#012676',
                color: '#012676',
                bgcolor: '#fff',
                '&:hover': { borderColor: '#011a5c', color: '#011a5c', bgcolor: 'rgba(1,38,118,0.04)' },
              }}
            >
              Clear Form
            </Button>
            <Button
              variant="contained"
              size="medium"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!patient || saveStatus === 'saving'}
              sx={{ bgcolor: '#012676', color: '#fff', '&:hover': { bgcolor: '#011a5c' } }}
            >
              {saveStatus === 'saving' ? 'Saving...' : 'Save Pre-Consultation'}
            </Button>
          </Box>

          {saveStatus === 'success' && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSaveStatus('idle')}>
              {saveMessage}
            </Alert>
          )}
          {saveStatus === 'error' && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveStatus('idle')}>
              {saveMessage}
            </Alert>
          )}

          <Typography variant="caption" sx={{ color: '#757575', display: 'block', mt: 2 }}>
            Note: Fields marked with &apos;*&apos; are required.
          </Typography>

        </Box>

        </Paper>

        <AmbientMicButton placement="fixed" />
      </Box>

      {/* ── RIGHT: shared Right Panel ── */}
      <Box
        sx={{
          width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH },
          flexShrink: 0,
          flex: { xs: '1 1 0%', md: '0 0 auto' },
          minHeight: 0,
          height: '100%',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <RightPanelTabs
          key={patient?.patient_id != null ? String(patient.patient_id) : 'no-patient'}
          defaultTab="summary"
          sessionId={session?.session_id ?? null}
          showCurrentVisitContent={!!session && session.status === 'active'}
        />
      </Box>
    </Box>
  );
}